interface IAudit {
  created: string;
  createdBy: string;
  lastModified: string;
  lastModifiedBy: string;
}

export interface IFranchiseDeleteModal {
  id: string;
  name: string;
}

/**
 * REST Headers for API endpoints
 */
export interface IRestHeaders {
  userId?: string;
  actingAsUserId?: string;
}

/**
 * Franchise Interfaces
 */
export interface IFranchiseResponse {
  data: IFranchiseDataIncoming | IFranchiseDataIncoming[];
  success: boolean;
  error?: IFranchiseResponseError;
}

export interface IFranchiseDataIncoming {
  audit: IAudit;
  id: string;
  name: string;
  abbreviation: string;
  userId: string;
  actingAsUserId: string;
}

export interface IFranchiseTableData {
  id: string;
  name: string;
  abbreviation: string;
  created: string;
  lastModified: string;
  formattedCreated: string;
  formattedLastModified: string;
}

export interface IFranchiseSelectorData {
  id: string;
  name: string;
  abbreviation: string;
}

export interface IFranchisePaginationTableData {
  data: IFranchiseTableData[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

export interface ICreateFranchiseRequest {
  name: string;
  abbreviation: string;
  userId?: string;
  actingAsUserId?: string;
}

export interface IFranchiseResponseError {
  status: string;
  message: string;
}

export type IDeleteFranchiseResponse = string;

/**
 * Interface for account franchise relationship
 */
export interface IAccountFranchiseMappingResponse {
  data: IFranchiseDataIncoming[];
  success: boolean;
  error?: IFranchiseResponseError;
}

export interface IUpdateAccountFranchiseDataRequest {
  accountId: string;
  franchiseNames: string[];
}

export interface IUpdateAccountFranchiseResponse {
  accountId: string;
  action: IAccountFranchiseMappingAction;
  successfulFranchiseMappings: string[];
  failedFranchiseNames: IFailedAccountFranchiseMapping[];
}

export type IAccountFranchiseMappingAction = "added" | "removed";

export interface IFailedAccountFranchiseMapping {
  franchiseName: string;
  reason: string;
}

/**
 * API paths for franchise endpoints
 * Using const assertion pattern instead of enum for better type safety
 */
export const FranchiseApiPaths = {
  GET_ALL_FRANCHISES: "/v1/franchise/",
  GET_FRANCHISE_BY_ID: "/v1/franchise/{id}",
  CREATE_FRANCHISE: "/v1/franchise/",
  DELETE_FRANCHISE: "/v1/franchise/{id}",

  GET_FRANCHISES_FOR_ACCOUNT: "/v1/accountFranchise/list/accountId/{accountId}",
  ADD_FRANCHISES_TO_ACCOUNT: "/v1/accountFranchise/add/accountId/{accountId}",
  REMOVE_FRANCHISES_FROM_ACCOUNT:
    "/v1/accountFranchise/remove/accountId/{accountId}"
} as const;

export const FranchiseLambdaRoutes = {
  GET_ALL_FRANCHISES: {
    method: "GET",
    path: "/franchiseManagement/franchise"
  },
  GET_FRANCHISE_BY_ID: {
    method: "GET",
    path: "/franchiseManagement/franchise/{id}"
  },
  CREATE_FRANCHISE: {
    method: "POST",
    path: "/franchiseManagement/franchise"
  },
  DELETE_FRANCHISE: {
    method: "DELETE",
    path: "/franchiseManagement/franchise/{id}"
  },
  GET_FRANCHISES_FOR_ACCOUNT: {
    method: "GET",
    path: "/franchiseManagement/accountFranchise/list/accountId/{accountId}"
  },
  ADD_FRANCHISES_TO_ACCOUNT: {
    method: "PUT",
    path: "/franchiseManagement/accountFranchise/add/accountId/{accountId}"
  },
  REMOVE_FRANCHISES_FROM_ACCOUNT: {
    method: "PUT",
    path: "/franchiseManagement/accountFranchise/remove/accountId/{accountId}"
  },
  GET_AUDIT_LOG: {
    method: "GET",
    path: "/franchiseManagement/auditLog"
  }
};
// Create a type from the object values for use in function parameters
export type FranchiseApiPathsType =
  (typeof FranchiseApiPaths)[keyof typeof FranchiseApiPaths];

/**
 * Validation Constraints
 * These are documented here for reference but are enforced directly in the validators
 */
export const ValidationConstraints = {
  FRANCHISE_CREATE: {
    NAME_NOT_EMPTY: "Franchise name can not be empty",
    NAME_ALPHANUMERIC_ONLY:
      "Franchise name must contain only letters and numbers",
    NAME_MAX_LENGTH: "Franchise name must be 50 characters or less",
    NO_DUPLICATE_NAMES: "Franchise name must be unique",
    USERID_ALPHANUMERIC_MAX_50:
      "User ID must be alphanumeric and maximum 50 characters",
    ABBREVIATION_NOT_EMPTY: "Abbreviation can not be empty",
    ABBREVIATION_MAX_LENGTH: "Abbreviation must be 2 characters or less",
    ABBREVIATION_ALPHANUMERIC_ONLY:
      "Abbreviation must contain only letters and numbers"
  },
  FRANCHISE_DELETE: {
    FRANCHISE_MUST_EXIST: "Franchise must exist to be deleted",
    NO_ACTIVE_ACCOUNT_RELATIONSHIPS:
      "Cannot delete franchise with active account relationships",
    USERID_ALPHANUMERIC_MAX_50:
      "User ID must be alphanumeric and maximum 50 characters"
  },
  // Account franchise mapping constraints
  ACCOUNT_FRANCHISE_ADD: {
    ACCOUNT_ID_URL_MATCH_BODY:
      "Account ID in URL must match account ID in request body",
    FRANCHISE_NAMES_REQUIRED: "At least one franchise name must be provided",
    FRANCHISE_NAME_NOT_EMPTY: "Franchise name at index {index} cannot be empty",
    USERID_ALPHANUMERIC_MAX_50:
      "User ID must be alphanumeric and maximum 50 characters"
  },
  // Account franchise removal constraints
  ACCOUNT_FRANCHISE_REMOVE: {
    ACCOUNT_ID_URL_MATCH_BODY:
      "Account ID in URL must match account ID in request body",
    FRANCHISE_NAMES_REQUIRED: "At least one franchise name must be provided",
    FRANCHISE_NAME_NOT_EMPTY: "Franchise name at index {index} cannot be empty",
    USERID_ALPHANUMERIC_MAX_50:
      "User ID must be alphanumeric and maximum 50 characters"
  }
};

export interface IHandlerResponseFailure {
  statusCode: number;
  body: string;
}

export interface IHandlerResponseSuccess {
  statusCode: number;
  body?: string;
  headers: Record<string, string>;
}

export type IHandlerResponse =
  | IHandlerResponseFailure
  | IHandlerResponseSuccess;

/**
 * Interface for audit log entries
 * Fields returned from account-franchise-audit-trail Lambda
 */
export interface IAuditLogEntry {
  eventId: string;
  accountId: string;
  franchiseName: string;
  actionType: string;
  modifiedDate: string;
  modifiedBy: string;
  formattedModifiedDate?: string; // Pre-formatted date for display
}

export * from "./ddc-pages-franchise-management-interface-mock";
